import { useState, useEffect } from 'react';
import { startRecording, saveRecording } from './controller';
import useRootStore from '../../hooks/useRootStore';
import getBlobDuration from '../../helper/getBlobDuration';

const initialState = {
    recordingMinutes: 0,
    recordingSeconds: 0,
    initRecording: false,
    mediaStream: null,
    mediaRecorder: null,
    audio: null,
};

type RecoderStateType = {
    recordingMinutes: number;
    recordingSeconds: number;
    initRecording: boolean;
    mediaStream: null | any;
    mediaRecorder: null | any;
    audio: null | any;
};

const useRecorder = () => {
    const [recorderState, setRecorderState] = useState<RecoderStateType>(initialState);
    const { readFile, setDurationMedia } = useRootStore().messageStore;

    useEffect(() => {
        const MAX_RECORDER_TIME = 5;
        let recordingInterval: any = null;

        if (recorderState.initRecording)
            recordingInterval = setInterval(() => {
                setRecorderState((prevState: any) => {
                    if (prevState.recordingMinutes === MAX_RECORDER_TIME && prevState.recordingSeconds === 0) {
                        clearInterval(recordingInterval);
                        return prevState;
                    }

                    if (prevState.recordingSeconds >= 0 && prevState.recordingSeconds < 59) {
                        return {
                            ...prevState,
                            recordingSeconds: prevState.recordingSeconds + 1,
                        };
                    }
                    if (prevState.recordingSeconds === 59) {
                        return {
                            ...prevState,
                            recordingMinutes: prevState.recordingMinutes + 1,
                            recordingSeconds: 0,
                        };
                    }
                });
            }, 1000);
        else clearInterval(recordingInterval);

        return () => clearInterval(recordingInterval);
    });

    useEffect(() => {
        if (recorderState.mediaStream)
            setRecorderState((prevState: any) => {
                return {
                    ...prevState,
                    mediaRecorder: new MediaRecorder(prevState.mediaStream),
                };
            });
    }, [recorderState.mediaStream]);

    useEffect(() => {
        const recorder: any = recorderState.mediaRecorder;
        let chunks: any = [];

        if (recorder && recorder.state === 'inactive') {
            recorder.start();

            recorder.ondataavailable = (e: any) => {
                chunks.push(e.data);
            };

            recorder.onstop = async () => {
                const blob = new Blob(chunks, { type: 'audio/ogg; codecs=opus' });
                const audioFile = new File([blob], 'voice.wav', { type: 'audio/wav' });
                const duration = await getBlobDuration(blob);
                setDurationMedia(Math.round(duration));
                readFile(audioFile, 'audio');

                chunks = [];

                setRecorderState((prevState: RecoderStateType) => {
                    if (prevState.mediaRecorder)
                        return {
                            ...initialState,
                            audio: window.URL.createObjectURL(blob),
                        };
                    else return initialState;
                });
            };
        }

        return () => {
            if (recorder) recorder.stream.getAudioTracks().forEach((track: any) => track.stop());
        };
    }, [recorderState.mediaRecorder, readFile]);

    return {
        recorderState,
        startRecording: () => startRecording(setRecorderState),
        cancelRecording: () => setRecorderState(initialState),
        saveRecording: () => saveRecording(recorderState.mediaRecorder),
    };
};

export default useRecorder;
